import api from './index';

// Messenger API — agent <-> buyer human messaging + AI copilot
export const messengerApi = {
  // `view` is the header-dropdown filter: 'inbox' (default), 'archived', 'deleted'.
  getInbox: async ({ tab = 'people', since, view } = {}) => {
    const params = { tab };
    if (since) params.since = since;
    if (view && view !== 'inbox') params.view = view;
    const res = await api.get('/api/chatbot/agent/inbox/', { params });
    return res.data;
  },

  bulkInboxAction: async (action) => {
    const res = await api.post('/api/chatbot/agent/inbox/bulk-action/', { action });
    return res.data;
  },

  getThread: async (sessionId, { afterId } = {}) => {
    const params = {};
    if (afterId) params.after_id = afterId;
    const res = await api.get(`/api/chatbot/agent/sessions/${sessionId}/`, { params });
    return res.data;
  },

  sendMessage: async (sessionId, { content, usedSuggestion, attachment } = {}) => {
    if (attachment) {
      // Multipart path — used when an image/file is attached. axios sets the
      // multipart boundary automatically once the body is a FormData.
      const fd = new FormData();
      if (content) fd.append('content', content);
      if (usedSuggestion) fd.append('used_suggestion', usedSuggestion);
      fd.append('attachment', attachment);
      // Override the axios instance default `application/json` for file uploads.
      const res = await api.post(
        `/api/chatbot/agent/sessions/${sessionId}/messages/`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return res.data;
    }
    const res = await api.post(`/api/chatbot/agent/sessions/${sessionId}/messages/`, {
      content,
      used_suggestion: usedSuggestion || null,
    });
    return res.data;
  },

  // Right-click context-menu actions on an inbox row.
  // action: 'archive' | 'unarchive' | 'mark_unread' | 'delete'
  sessionAction: async (sessionId, action) => {
    const res = await api.post(`/api/chatbot/agent/sessions/${sessionId}/action/`, { action });
    return res.data;
  },

  reactToMessage: async (messageId, emoji) => {
    const res = await api.post(`/api/chatbot/agent/messages/${messageId}/react/`, { emoji });
    return res.data;
  },

  // Edit your own message (server enforces the 1-hour window + sender check).
  editMessage: async (messageId, content) => {
    const res = await api.post(`/api/chatbot/agent/messages/${messageId}/edit/`, { content });
    return res.data;
  },

  // Soft-delete your own message — leaves a "deleted" tombstone in the thread.
  deleteMessage: async (messageId) => {
    const res = await api.post(`/api/chatbot/agent/messages/${messageId}/delete/`);
    return res.data;
  },

  getAiContext: async (sessionId, { refresh = false } = {}) => {
    const params = refresh ? { refresh: 1 } : {};
    const res = await api.get(`/api/chatbot/agent/sessions/${sessionId}/ai-context/`, { params });
    return res.data;
  },

  askAi: async (sessionId, { question, history = [] } = {}) => {
    const res = await api.post(`/api/chatbot/agent/sessions/${sessionId}/ai-ask/`, {
      question,
      history,
    });
    return res.data;
  },

  escalate: async (sessionId, { toUserId } = {}) => {
    const res = await api.post(`/api/chatbot/agent/sessions/${sessionId}/escalate/`, {
      to_user_id: toUserId || null,
    });
    return res.data;
  },

  setProperty: async (sessionId, propertyId) => {
    const res = await api.post(`/api/chatbot/agent/sessions/${sessionId}/property/`, {
      property_id: propertyId,
    });
    return res.data;
  },

  setTyping: async (sessionId, typing) => {
    // Fire-and-forget — typing pings are best-effort and shouldn't throw out
    // of the composer. Swallow errors at the call-site.
    await api.post(`/api/chatbot/agent/sessions/${sessionId}/typing/`, { typing: !!typing });
  },
};

export default messengerApi;
